import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { validateFirstName, validateLastName, validatePassword, validatePasswordConfirmation } from '../validation/authSchemas';
import { useFormValidation } from '../hooks/useFormValidation';
import { authService } from '../services/authService';
import { useApp } from '../context/AppContext';

interface RegisterFormProps {
  email: string;
  onBack: () => void;
  onSuccess: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  email,
  onBack,
  onSuccess,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { errors, validateField, validateFieldWithDependency, clearError, hasErrors } = useFormValidation();
  const { dispatch } = useApp();
  const formRef = useRef<HTMLFormElement>(null);

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFirstName(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('firstName', value, validateFirstName);
    } else {
      clearError('firstName');
    }
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLastName(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('lastName', value, validateLastName);
    } else {
      clearError('lastName');
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPassword(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('password', value, validatePassword);
      // Re-validate confirm password when password changes
      if (confirmPassword.trim()) {
        validateFieldWithDependency('confirmPassword', confirmPassword, validatePasswordConfirmation, value);
      }
    } else {
      clearError('password');
      clearError('confirmPassword');
    }
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setConfirmPassword(value);
    setApiError(null);
    
    if (value.trim()) {
      validateFieldWithDependency('confirmPassword', value, validatePasswordConfirmation, password);
    } else {
      clearError('confirmPassword');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (hasErrors || !firstName.trim() || !lastName.trim() || !password.trim() || !confirmPassword.trim()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await authService.register(email, password, firstName, lastName);
      
      // New users are created with USER role by default
      // Admin privileges must be granted by an existing admin
      if (!authService.isAdmin(response.data.user)) {
        throw new Error('Admin privileges not granted. Please contact an existing admin to grant you access.');
      }
      
      // Store token and user data
      authService.setToken(response.data.token);
      dispatch({
        type: 'SET_USER',
        payload: response.data.user,
      });
      
      onSuccess();
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle form submission
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSubmit(event);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        background: 'linear-gradient(135deg, #173B67 0%, #3478C8 100%)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" color="primary">
            Create Admin Account
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Complete your admin registration
        </Typography>

        <form 
          ref={formRef}
          onSubmit={handleFormSubmit} 
          method="post" 
          noValidate 
          autoComplete="on"
        >
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Admin Email Address"
            type="email"
            value={email}
            name="email"
            autoComplete="email"
            sx={{ mb: 3 }}
            helperText="Admin email address (cannot be changed)"
            InputProps={{
              readOnly: true,
            }}
          />

          <TextField
            fullWidth
            label="First Name"
            value={firstName}
            onChange={handleFirstNameChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={isLoading}
            sx={{ mb: 3 }}
            name="firstName"
            autoComplete="given-name"
            autoFocus
          />

          <TextField
            fullWidth
            label="Last Name"
            value={lastName}
            onChange={handleLastNameChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={isLoading}
            sx={{ mb: 3 }}
            name="lastName"
            autoComplete="family-name"
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            error={!!errors.password}
            helperText={errors.password || 'Must be at least 8 characters with uppercase, lowercase, and number'}
            disabled={isLoading}
            sx={{ mb: 3 }}
            autoComplete="new-password"
            name="password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={isLoading}
            sx={{ mb: 3 }}
            autoComplete="new-password"
            name="confirmPassword"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || hasErrors || !firstName.trim() || !lastName.trim() || !password.trim() || !confirmPassword.trim()}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Create Admin Account'
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            Already have an admin account?{' '}
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={onBack}
            >
              Go back to sign in
            </Typography>
          </Typography>
        </form>
      </Paper>
    </Box>
  );
};
